import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchEmployee } from "../../api/employee";
import { selectJobCodeData } from "../../slices/commonConfig/commonConfigSlice";
import { validateEmployeeDataForBidGroup } from "../../util/bid/bidInputValidation";
import { buildAddEmployeeData } from "../../util/bid/bidGroupUtils";
import { BID_TYPE } from "../../util/bid/bidConstants";
import { copyJsonObject } from "../../util/commonUtils.js";
import { BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT, EMP_SCOPE } from "../../util/constants";
import { validateEmployee } from "../../util/inputValidation";
import { CHAR_LIMIT_MESSAGE, COMMENTS_REQUIRED } from "../../util/messageConstants";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { getFreedayStandardReliefMap } from "../../api/scheduleEditor.js";

const useStyles = makeStyles((theme) => ({
  inputText: {
    width: "15vw",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
    marginTop: "20px",
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "14px",
    marginTop: "-5px",
  },
  commentBox: {
    marginTop: "25px",
  },
  employeeInfoLoading: {
    position: "relative",
    float: "left",
    top: "7px",
    marginLeft: "3vw",
  },
}));

const ReAddEmployeeDialog = (props) => {
  const classes = useStyles();

  //redux
  const jobCodeData = useSelector(selectJobCodeData);

  // employee data state
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeIdSearchLoading, setEmployeeIdSearchLoading] = useState(false);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");

  //comments
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");

  //fetch employee latest data
  useEffect(() => {
    if (props?.reAddEmployeeData?.eid) {
      fetchEmployeeData(props?.reAddEmployeeData.eid);
    }
    // eslint-disable-next-line
  }, [props?.reAddEmployeeData?.eid]);

  const getApplicableDeclaration = () => {
    return employeeData?.declarations?.find((declarationItem) => declarationItem.year === props.schedYear)?.declaration;
  };

  const handleCommentsChange = (event) => {
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments(event.target.value);
  };

  const handleCancelClicked = () => {
    props.setReAddEmployeeData(null);
    setComments("");
    setCommentsError(false);
    setCommentErrorMessage("");
    props.onClickDialogClose();
  };

  const handleAddClicked = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT));
    } else {
      const copiedBidGroup = copyJsonObject(props.bidGroup);

      // Find the index of the employee with given eid and remove from the list
      const employeePosition = copiedBidGroup.findIndex((emp) => emp.eid === employeeData.eid);
      if (employeePosition !== -1) {
        copiedBidGroup.splice(employeePosition, 1);
      }

      const commentsArray = [
        {
          comments,
          createdAt: DateTime.now().toMillis().toString(),
          createdBy: `${props.userSession.lastName}, ${props.userSession.firstName}`,
          isEligible: true,
          rank: "-",
          isRemoved: false,
        },
        ...(Array.isArray(props?.reAddEmployeeData?.comments) ? props.reAddEmployeeData.comments : []),
      ];

      // add the same employee to the top of the list
      props.setBidGroup([
        buildAddEmployeeData(employeeData, commentsArray, props.bidStatus, props.bidType),
        ...copiedBidGroup,
      ]);
      props.onClickDialogClose();
      props.setReAddEmployeeData(null);
      setCommentsError(false);
      setComments("");
      // scroll to top of the grid once addition is done to see added item.
      setTimeout(() => {
        props.apiRef.current.scrollToIndexes({
          rowIndex: 0,
        });
      });
    }
  };

  const fetchEmployeeData = async (eid) => {
    setEmployeeIdSearchLoading(true);
    setEmployeeIdErrors("");
    setEmployeeData(null);
    const scope =
      props.bidType === BID_TYPE.FREEDAY || props.bidType === BID_TYPE.TWU_DAT_FTO ? EMP_SCOPE.DECLARATIONS : "";
    await fetchEmployee(eid, scope)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let fetchedEmployeeData = result;
          if (props.bidType === BID_TYPE.FREEDAY) {
            const standardReliefMap = await getFreedayStandardReliefMap(
              props.bidStation,
              props.bidWorkgroup,
              props.schedMonth,
              props.schedYear,
              fetchedEmployeeData.eid
            );
            let standardRelief = "";
            if (standardReliefMap && Object.keys(standardReliefMap).includes(fetchedEmployeeData.eid)) {
              standardRelief = standardReliefMap[fetchedEmployeeData.eid];
            }
            fetchedEmployeeData = {
              ...fetchedEmployeeData,
              standardRelief: standardRelief,
            };
          }
          let employeeDataErrors = validateEmployeeDataForBidGroup(fetchedEmployeeData);
          if (employeeDataErrors === "") {
            employeeDataErrors = validateEmployee(
              fetchedEmployeeData.eid,
              fetchedEmployeeData.shortName,
              fetchedEmployeeData.lastName,
              fetchedEmployeeData.activeStation,
              jobCodeData[fetchedEmployeeData.homeAccountJobCode].workgroup
            );
          }
          if (employeeDataErrors !== "") {
            setEmployeeIdErrors(employeeDataErrors);
          }
          setEmployeeData(fetchedEmployeeData);
          setEmployeeIdSearchLoading(false);
        } else {
          setEmployeeIdErrors("Invalid Employee ID");
          setEmployeeIdSearchLoading(false);
        }
      })
      .catch((err) => {
        setEmployeeIdErrors("Error while fetching employee details");
        setEmployeeIdSearchLoading(false);
      });
  };

  return (
    <Dialog
      fullWidth
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={handleCancelClicked}
      maxWidth={"md"}
      sx={{ margin: "auto" }}
    >
      <Box p={1}>
        <DialogTitle>Add Employee</DialogTitle>
        <DialogContent>
          <Typography fontSize={"14px"}>
            Adding Employees to the Bid Group will not affect the Seniority List.
          </Typography>
          <Grid container>
            <TextField
              variant="outlined"
              className={classes.inputText}
              label="Employee ID"
              value={props?.reAddEmployeeData?.eid || ""}
              disabled
              error={employeeIdErrors !== ""}
              helperText={employeeIdErrors}
            />
          </Grid>
          {!employeeIdSearchLoading ? (
            employeeData &&
            !employeeIdErrors && (
              <Grid
                container
                alignItems={"center"}
                justifyContent={"center"}
                sx={{
                  marginTop: 3,
                  backgroundColor: "secondary.swaGray1",
                  height: "auto",
                  borderRadius: "5px",
                }}
              >
                <Grid item xs={4} sx={{ fontSize: "16px", paddingLeft: "16px", textAlign: "left" }}>
                  {employeeData.lastName + ", " + employeeData.shortName}
                </Grid>

                <Grid item xs={8} sx={{ textAlign: "left", maxHeight: "56px" }}>
                  <Stack direction="row">
                    <Box ml={2} mr={2}>
                      <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                        HOME STATION
                      </Typography>
                      <p className={classes.employeeDataValue}>{employeeData.homeStation}</p>
                    </Box>
                    {employeeData.tempStation && (
                      <Box ml={2} mr={2}>
                        <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                          TEMP STATION
                        </Typography>
                        <p className={classes.employeeDataValue}>{employeeData.tempStation}</p>
                      </Box>
                    )}
                    <Box ml={2} mr={2}>
                      <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                        WORKGROUP
                      </Typography>
                      <p className={classes.employeeDataValue}>
                        {getWorkgroupGivenJobCode(employeeData.homeAccountJobCode)}
                      </p>
                    </Box>
                    <Box ml={2} mr={2}>
                      <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                        STATUS
                      </Typography>
                      <p className={classes.employeeDataValue}>{employeeData.ftpt}</p>
                    </Box>
                    {(props.bidType === BID_TYPE.FREEDAY || props.bidType === BID_TYPE.TWU_DAT_FTO) && (
                      <Box ml={2} mr={2}>
                        <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                          DECLARATION
                        </Typography>
                        <p className={classes.employeeDataValue}>{getApplicableDeclaration() || "-"}</p>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            )
          ) : (
            <CircularProgress className={classes.employeeInfoLoading} size={24} />
          )}
          <TextField
            className={classes.commentBox}
            value={comments}
            multiline
            fullWidth
            rows={2}
            label="Comments"
            placeholder="Enter comments here"
            InputLabelProps={{ shrink: true }}
            onChange={handleCommentsChange}
            error={commentsError}
            helperText={commentErrorMessage}
          />
          {comments.length > BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT ? (
            <Box sx={{ textAlign: "right", color: "primary.swaRed" }}>
              {comments.length} / {BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT}
            </Box>
          ) : (
            <Box sx={{ textAlign: "right" }}>
              {comments.length} / {BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            id="cancelButton"
            sx={{ width: "100px" }}
            variant="outlined"
            color="primary"
            onClick={handleCancelClicked}
            disabled={!!employeeIdErrors || employeeIdSearchLoading}
            size="large"
          >
            Cancel
          </Button>
          <Button
            id="addButton"
            sx={{ width: "100px" }}
            variant="contained"
            color="primary"
            onClick={handleAddClicked}
            disabled={!!employeeIdErrors || employeeIdSearchLoading}
            size="large"
          >
            Add
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

ReAddEmployeeDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  bidGroup: PropTypes.array.isRequired,
  setBidGroup: PropTypes.func.isRequired,
  reAddEmployeeData: PropTypes.object,
  setReAddEmployeeData: PropTypes.func.isRequired,
  apiRef: PropTypes.object.isRequired,
  bidStatus: PropTypes.string.isRequired,
  userSession: PropTypes.object.isRequired,
  bidType: PropTypes.string.isRequired,
  schedYear: PropTypes.string,
  schedMonth: PropTypes.string,
  bidStation: PropTypes.string,
  bidWorkgroup: PropTypes.string,
};

export default ReAddEmployeeDialog;
